import { useEffect, useState } from 'react';
import { Container, Dropdown, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getSlotGamesReq } from 'stores/auth/services';
import Loading from 'components/general/loading';
import SlotItem from './SlotItem';
import _ from 'lodash';

const defaultProviderOrder = [
  'allproviders',
  'pragmatic',
  'amatic',
  'novomatic',
  'merkur',
  'egt'
]

export default function Slot() {
  const { t } = useTranslation('translations');
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState([
    {
      title: t('allproviders'),
      key: 'allproviders',
      games: []
    }
  ]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const getData = async () => {
    try {
      setLoading(true);
      const response = await getSlotGamesReq();
      const tempProviders = [
        {
          title: t('allproviders'),
          key: 'allproviders',
          games: []
        }
      ];
      const games = [...response.extraGames, ...Object.values(response.games)];
      response.providers = _.uniqBy(games, 'vendor').map((g) => g.vendor);
      tempProviders.push(
        ...response.providers.map((p) => ({
          title: p,
          key: p,
          games: []
        }))
      );

      // Get popular games and providers from response
      let popularGames = [];
      let popularProviders = [];
      let providerOrder = [...defaultProviderOrder];

      if (response.popularGames && response.popularGames.popularGames) {
        popularGames = response.popularGames.popularGames;
        popularProviders = response.popularGames.popularProviders || [];
        
        // Add popular providers to the beginning of provider order
        const orderedPopularProviders = popularProviders.filter(p => 
          !defaultProviderOrder.some(dpo => 
            dpo.trim().toLowerCase() === p.trim().toLowerCase()
          )
        );
        providerOrder = ['allproviders', ...orderedPopularProviders, ...defaultProviderOrder.slice(1)];
      }

      // Add popular games to "All Providers" section
      games.forEach((game) => {
        if (popularGames.includes(game.id_game)) {
          tempProviders[0].games.push(game);
        }
        const providerObjIndex = tempProviders.findIndex((p) => 
          p.key.trim().toLowerCase() === game.vendor.trim().toLowerCase()
        );
        if (providerObjIndex > -1) {
          tempProviders[providerObjIndex].games.push(game);
        }
      });

      // Sort games in allproviders section according to popularGames order
      if (popularGames.length > 0) {
        tempProviders[0].games.sort((a, b) => {
          const indexA = popularGames.indexOf(a.id_game);
          const indexB = popularGames.indexOf(b.id_game);
          // If both are in popular games, sort by their position in popularGames array
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          // If only one is popular, prioritize it
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          // If neither is popular, maintain original order
          return 0;
        });
      }

      // Sort providers according to providerOrder array
      tempProviders.sort((a, b) => {
        const indexA = providerOrder.findIndex(p => 
          p.trim().toLowerCase() === a.key.trim().toLowerCase()
        );
        const indexB = providerOrder.findIndex(p => 
          p.trim().toLowerCase() === b.key.trim().toLowerCase()
        );

        // If both providers are in the order array, sort by their position
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        // If only one is in the order array, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        // If neither is in the order array, sort alphabetically
        return a.title.localeCompare(b.title);
      });

      tempProviders.forEach((p) => {
        p.title = p.title.toUpperCase();
      });
      setProviders(tempProviders);
      setSelectedProvider(tempProviders[0]);
    } catch (error) {
      console.log(error);
      // error
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const filterProviderBySearch = (provider) => {
    return search.trim() !== ''
      ? provider.games.filter((g) => g.display_name.toLowerCase().includes(search.toLowerCase()))
          .length > 0
      : true;
  };

  const filterProviderGamesBySearch = (game) => {
    return search.trim() !== ''
      ? game.display_name.toLowerCase().includes(search.toLowerCase())
      : true;
  };

  if (loading) return <Loading message={t('loading_games')} />;

  return (
    <Container id="slot-container" style={{ backgroundColor: '#171717', maxWidth: '100%' }}>
      <div className="row py-2 gy-3">
        <div className="col-6">
          <Form.Control
            type="text"
            placeholder={t('search_games')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-6">
          <Dropdown align={'end'}>
            <Dropdown.Toggle
              variant=""
              id="dropdown-providers"
              className="w-100 flex-center border">
              <span className="w-100 text-start text-capitalize" style={{ color: 'white' }}>
                {selectedProvider.title ?? t('allproviders')}
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ maxHeight: 300, overflowY: 'auto' }}>
              {providers.map((provider) => (
                <Dropdown.Item
                  key={provider.key}
                  onClick={() => setSelectedProvider(provider)}
                  className="text-capitalize">
                  {provider?.title}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <hr className="col-12 mb-0" />
        {providers
          .filter((p) =>
            selectedProvider?.key === 'allproviders' ? true : p.key === selectedProvider.key
          )
          .filter((p) => filterProviderBySearch(p))
          .map((provider, i) => (
            <div
              className="col-12 col-md-4"
              key={`slot-item-${provider.key}`}
              style={{ padding: 0 }}>
              <SlotItem
                provider={provider?.title}
                games={provider.games.filter((g) => filterProviderGamesBySearch(g))}
              />
            </div>
          ))}
      </div>
    </Container>
  );
}
